import type { ComponentProps } from 'react';
import { createStyles, Skeleton } from '@mantine/core';

import UnknownErrorScreen from '~/domains/common/components/UnknownErrorScreen/UnknownErrorScreen';
import { isApiError } from '~/domains/services/utils/isApiError';

import type { PlayerInfo, PlayerGroupedStats } from '../../types/player';

import GamecenterPlayerDetailContent from './GamecenterPlayerDetailContent';
import GamecenterPlayerDetailModalContainer from './GamecenterPlayerDetailModalContainer';
import GamecenterPlayerDetailHeader from './GamecenterPlayerDetailHeader';
import type { GamecenterPlayerDetailActionProps } from './GamecenterPlayerDetailActions/GamecenterPlayerDetailAction';
import type GamecenterPlayerDetailGameLog from './GamecenterPlayerDetailGameLog';

const useStyles = createStyles((theme) => ({
  tempContainer: {
    padding: theme.other.spacing._16,
    display: 'flex',
    flexDirection: 'column',
    gap: theme.other.spacing._16,
  },
}));

type GamecenterPlayerDetailProps = {
  isOpen: boolean;
  onClose: () => void;
  isLoading?: boolean;
  error?: unknown;
  headerProps?: ComponentProps<typeof GamecenterPlayerDetailHeader>;
  playerInfo?: PlayerInfo;
  groupedStats?: PlayerGroupedStats;
  gameLogProps?: ComponentProps<typeof GamecenterPlayerDetailGameLog>;
  actions?: GamecenterPlayerDetailActionProps[];
  type?: 'survivor-prop' | 'others';
  propTypeDisplay?: string;
};

function GamecenterPlayerDetail({
  isOpen,
  onClose,
  isLoading,
  error,
  headerProps,
  playerInfo,
  groupedStats,
  gameLogProps,
  actions,
  type = 'others',
  propTypeDisplay,
}: GamecenterPlayerDetailProps) {
  const { classes } = useStyles();

  return (
    <GamecenterPlayerDetailModalContainer
      isOpen={isOpen}
      onClose={onClose}
      header={isLoading ? <Skeleton h={140} /> : <GamecenterPlayerDetailHeader {...headerProps} />}
    >
      {(() => {
        if (isLoading) {
          return (
            <div className={classes.tempContainer}>
              <Skeleton h={120} />
              <Skeleton h={150} />
            </div>
          );
        }
        if (error && isApiError(error)) {
          return (
            <div className={classes.tempContainer}>
              <UnknownErrorScreen statusCode={error.response?.status} />
            </div>
          );
        }

        return (
          <GamecenterPlayerDetailContent
            playerInfo={playerInfo}
            groupedStats={groupedStats}
            gameLogProps={gameLogProps}
            actions={actions}
            type={type}
            propTypeDisplay={propTypeDisplay}
          />
        );
      })()}
    </GamecenterPlayerDetailModalContainer>
  );
}

export default GamecenterPlayerDetail;
